import type { ApiError } from './types';

const TOKEN_KEY = 'portal.accessToken';

export function getStoredToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string | null) {
  if (token === null) sessionStorage.removeItem(TOKEN_KEY);
  else sessionStorage.setItem(TOKEN_KEY, token);
}

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly apiError: ApiError,
  ) {
    super(apiError.message);
    this.name = 'HttpError';
  }
}

interface RequestOptions extends RequestInit {
  authenticated?: boolean;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { authenticated = true, headers, ...rest } = options;
  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((headers as Record<string, string>) ?? {}),
  };
  if (authenticated) {
    const token = getStoredToken();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`/api${path}`, { ...rest, headers: finalHeaders });

  if (res.status === 204) return undefined as T;

  const body = await res.text();
  const json = body ? safeJsonParse(body) : null;

  if (!res.ok) {
    const apiError: ApiError =
      json && typeof json === 'object' && 'error' in json
        ? ((json as { error: ApiError }).error ?? { code: 'UNKNOWN', message: res.statusText })
        : { code: 'UNKNOWN', message: res.statusText || `HTTP ${res.status}` };

    if (res.status === 401) {
      setStoredToken(null);
      window.dispatchEvent(new CustomEvent('portal:unauthorized'));
    }
    throw new HttpError(res.status, apiError);
  }

  return json as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
